import { Button, CenteredLoader, ModuleHeader } from '@/components'
import { useBreadcrumbOverride } from '@/utils'
import { useTranslate } from '@ur-solutions/react-hooks'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { JsonContent } from './json-content'
import type { JsonValue } from './json-tree'
import { JsonTree } from './json-tree'

export interface TachoFileBrowserMetaItem {
  label: string
  value: ReactNode
}

interface TachoFileBrowserProps {
  /** File name shown as the page title and the final breadcrumb. */
  fileName?: string | null
  /** Raw `processedValue` from the API (a JSON string or already-parsed object). */
  processedValue: unknown
  loading: boolean
  /** Where the back button navigates (the originating file archive list). */
  backPath: string
  /** Metadata key/value pairs rendered under the header. Falsy values are skipped. */
  meta?: TachoFileBrowserMetaItem[]
}

/** Walk the parsed tree following a selection path (array indices look like `[3]`). */
const resolvePath = (root: JsonValue, path: string[]): JsonValue | undefined => {
  let current: JsonValue | undefined = root
  for (const segment of path) {
    if (current === null || typeof current !== 'object') return undefined
    const match = /^\[(\d+)\]$/.exec(segment)
    if (match && Array.isArray(current)) {
      current = current[Number(match[1])]
    } else if (!Array.isArray(current)) {
      current = current[segment]
    } else {
      return undefined
    }
  }
  return current
}

const parseProcessedValue = (raw: unknown): JsonValue | null => {
  if (raw == null) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as JsonValue
    } catch {
      return null
    }
  }
  return raw as JsonValue
}

/**
 * Folder-tree browser for the parsed content of a tacho file. Shared between the
 * vehicle (mass memory) and driver (card) file archives — only the data source
 * and the metadata header differ between them.
 */
export const TachoFileBrowser: React.FC<TachoFileBrowserProps> = ({
  fileName,
  processedValue,
  loading,
  backPath,
  meta,
}) => {
  const translations = useTranslate({
    fileArchive: 'activities.file-archive',
    back: 'common.back',
    noContent: 'common.no-data',
    selectItem: 'common.select',
  })

  const navigate = useNavigate()
  const location = useLocation()

  // Show the file name as the final breadcrumb (falls back to a loading label
  // while the file is being fetched). The breadcrumb is keyed by the raw last
  // path segment — derived the same way the breadcrumb component splits the
  // pathname — so the override matches even though the id segment is a base64
  // global id (the decoded `useParams` value would not match).
  const breadcrumbKey =
    location.pathname.split('/').filter(Boolean).pop() ?? null
  useBreadcrumbOverride(breadcrumbKey, fileName ?? null)

  const parsed = useMemo(
    () => parseProcessedValue(processedValue),
    [processedValue]
  )

  const [selectedPath, setSelectedPath] = useState<string[] | null>(null)

  const selectedValue =
    parsed && selectedPath ? resolvePath(parsed, selectedPath) : undefined

  const metaItems = meta?.filter(item => !!item.value) ?? []

  return (
    <div className="relative p-4 md:px-[52px] md:pt-8 md:pb-[52px]">
      <ModuleHeader
        className="flex-row items-center gap-4 whitespace-nowrap"
        contentClassName="items-center gap-4"
        title={fileName ?? translations.fileArchive}
        loading={loading}
      >
        <Button variant="outline" size="sm" onClick={() => navigate(backPath)}>
          <ArrowLeft className="size-4" />
          {translations.back}
        </Button>
      </ModuleHeader>

      {metaItems.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 pl-2 text-sm text-muted-foreground md:pl-0">
          {metaItems.map(item => (
            <span key={item.label}>
              {item.label}:{' '}
              <span className="text-foreground">{item.value}</span>
            </span>
          ))}
        </div>
      )}

      {loading && !parsed ? (
        <CenteredLoader />
      ) : !parsed ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-muted-foreground">
          {translations.noContent}
        </div>
      ) : (
        <div className="flex h-[calc(100vh-18rem)] min-h-[28rem] overflow-hidden rounded-xl border border-border bg-card">
          <div className="w-80 shrink-0 overflow-auto border-r border-border p-2">
            <JsonTree
              data={parsed}
              selected={selectedPath ?? []}
              onSelect={setSelectedPath}
            />
          </div>
          <div className="min-w-0 flex-1 overflow-hidden p-4">
            {selectedPath && selectedValue !== undefined ? (
              <JsonContent path={selectedPath} value={selectedValue} />
            ) : (
              <div className="flex h-full items-center justify-center text-center text-muted-foreground">
                {translations.selectItem}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
